// window.PlConn — the page's link to the server while an exercise runs
// (docs/project/restart-notice-and-resend.md). Two jobs, one file, because they share the banner:
//
// 1. KEEP A RECORD UNTIL THE SERVER HAS IT. Practice and Learn send one small record per event (an
//    answer, a card shown, the session's start and end). Until 2026-10-15 each was sent once and a
//    failure was swallowed — a crash, a slow restart or the learner's own network dropping cost
//    those answers without a word. PlConn.post keeps a record that could not be sent, tries again
//    (2 s, 4 s, 8 s, then every 15 s while the page is open) and saves the list in this browser, so
//    a reload or a closed tab sends it on the next visit. Order is kept: nothing overtakes a record
//    still waiting, and a session end (barrier) goes only once everything before it is in.
//    Every record carries a random recordId, so a copy that arrives twice — saved, but the reply
//    lost — is skipped by the server (ClientRecordGuard).
//
// 2. SAY WHEN THE APP IS RESTARTING. GET /app-status every 30 s; "begins" (the deploy script
//    says it no less than 45 s before it stops the app) shows "Parlevi is restarting. Please wait
//    a minute." and stops new exercises from starting. A freshly started server says "running",
//    which clears it all. No notice BEFORE that (owner, 2026-10-15): it gave the learner nothing
//    to do. A "pending" word from the server is still read — it makes the page ask every 15 s —
//    but shows nothing.
//
// The banner shows only when something is at stake: the restart itself, or records that have been
// waiting more than 5 s. A blip nobody would have noticed stays silent.
(function () {
	"use strict";

	var STORE_KEY = "plUnsentRecords";
	var MAX_AGE_MS = 7 * 24 * 3600 * 1000;    // the server keeps its seen-ids twice as long
	var SEND_TIMEOUT_MS = 45000;               // longer than Caddy's 30 s hold through a restart
	var RETRY_STEPS_MS = [2000, 4000, 8000, 15000];
	var POLL_SLOW_MS = 30000;                  // deploy-command.sh's BEGINS_LEAD (45 s) must stay above it
	var POLL_FAST_MS = 15000;
	var BANNER_AFTER_MS = 5000;
	var TAB_ALIVE_MS = 60000;                  // a tab whose saved list is older than this is closed
	var HEARTBEAT_MS = 20000;

	// ── ids ──────────────────────────────────────────────────────────────────────────────────────
	function newId() {
		if (window.crypto && typeof window.crypto.randomUUID === "function") {
			return window.crypto.randomUUID();
		}
		var b = new Uint8Array(16);
		window.crypto.getRandomValues(b);
		b[6] = (b[6] & 0x0f) | 0x40;
		b[8] = (b[8] & 0x3f) | 0x80;
		var h = [];
		for (var i = 0; i < 16; i++) { h.push((b[i] + 0x100).toString(16).substring(1)); }
		return h.slice(0, 4).join("") + "-" + h.slice(4, 6).join("") + "-" + h.slice(6, 8).join("")
			+ "-" + h.slice(8, 10).join("") + "-" + h.slice(10).join("");
	}

	var tabId = newId();

	// ── the list of records not yet confirmed ────────────────────────────────────────────────────
	// state: "new" (ready to go) · "sending" · "waiting" (failed; goes again on the retry timer)
	var queue = [];
	var retryTimer = null;
	var retryStep = 0;
	var heartbeat = null;

	// A status the server may answer while it is not really there: no connection (0), a timeout,
	// Caddy giving up on a held request (502/504), the app refusing while it starts (503), the
	// limiter (429). Anything else is the server's own answer about THIS record and is final — a
	// record the server refuses is dropped, as every record was before.
	function retryable(status) {
		return status === 0 || status === 408 || status === 429 || status === 502
			|| status === 503 || status === 504;
	}

	function post(url, data, opts) {
		opts = opts || {};
		var rec = {
			id: newId(), url: url, data: data || {}, json: !!opts.json, barrier: !!opts.barrier,
			ts: Date.now(), state: "new", onSuccess: opts.onSuccess || null, d: $.Deferred()
		};
		queue.push(rec);
		pump();
		save();
		return rec.d.promise();
	}

	function pump() {
		for (var i = 0; i < queue.length; i++) {
			var r = queue[i];
			if (r.state === "waiting") { return; }      // nothing overtakes a record still waiting
			if (r.state === "sending") { continue; }
			if (r.barrier && i > 0) { return; }          // an end goes only after everything before it
			send(r);
			if (r.barrier) { return; }                   // …and nothing goes past an end in flight
		}
	}

	function send(rec) {
		rec.state = "sending";
		var url = rec.url;
		var body;
		var settings = { type: "POST", dataType: "text", timeout: SEND_TIMEOUT_MS };
		if (rec.json) {
			// A JSON body keeps the shape older pages also send; the id rides on the query.
			url += (url.indexOf("?") < 0 ? "?" : "&") + "recordId=" + encodeURIComponent(rec.id);
			body = JSON.stringify(rec.data);
			settings.contentType = "application/json";
		} else {
			body = $.extend({}, rec.data, { recordId: rec.id });
			settings.traditional = true;
		}
		settings.data = body;
		settings.success = function (text) { finish(rec, text); };
		settings.error = function (xhr) {
			var status = xhr ? xhr.status : 0;
			if (retryable(status)) {
				fail(rec);
			} else {
				drop(rec);
			}
		};
		$.ajax(url, settings);
	}

	function remove(rec) {
		var i = queue.indexOf(rec);
		if (i >= 0) { queue.splice(i, 1); }
	}

	function finish(rec, text) {
		remove(rec);
		rec.state = "done";
		if (rec.onSuccess) {
			var data = null;
			try { data = text ? JSON.parse(text) : null; } catch (e) { data = null; }
			try { rec.onSuccess(data); } catch (e) { /* a callback must not stall the list */ }
		}
		rec.d.resolve();
		if (!hasWaiting()) { retryStep = 0; }
		save();
		updateBanner();
		pump();
	}

	function drop(rec) {
		remove(rec);
		rec.state = "done";
		rec.d.resolve();   // waiters (the session end) must not hang on a record the server refused
		save();
		updateBanner();
		pump();
	}

	function fail(rec) {
		rec.state = "waiting";
		if (!rec.failedAt) { rec.failedAt = Date.now(); }
		save();
		scheduleRetry();
		setTimeout(updateBanner, BANNER_AFTER_MS + 50);
		pollSoon();
	}

	function hasWaiting() {
		for (var i = 0; i < queue.length; i++) {
			if (queue[i].state === "waiting") { return true; }
		}
		return false;
	}

	function waitingLong() {
		var now = Date.now();
		for (var i = 0; i < queue.length; i++) {
			if (queue[i].state === "waiting" && now - queue[i].failedAt >= BANNER_AFTER_MS) { return true; }
		}
		return false;
	}

	function scheduleRetry() {
		if (retryTimer) { return; }
		var delay = RETRY_STEPS_MS[Math.min(retryStep, RETRY_STEPS_MS.length - 1)];
		retryStep++;
		retryTimer = setTimeout(retryNow, delay);
	}

	function retryNow() {
		if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
		for (var i = 0; i < queue.length; i++) {
			if (queue[i].state === "waiting") { queue[i].state = "new"; }
		}
		pump();
	}

	// ── kept in this browser across a reload or a closed tab ─────────────────────────────────────
	// One entry per tab ({tabId: {ts, records}}), so two open tabs never overwrite each other's
	// list. A tab refreshes its own entry while it has records; an entry nobody has refreshed for a
	// minute belongs to a tab that is gone, and the next page to load takes it over. Two tabs
	// taking over the same entry at once only send a record twice — which the recordId makes safe.
	function readStore() {
		try {
			var raw = window.localStorage.getItem(STORE_KEY);
			var parsed = raw ? JSON.parse(raw) : {};
			return parsed && typeof parsed === "object" ? parsed : {};
		} catch (e) {
			return {};
		}
	}

	function writeStore(all) {
		try {
			if ($.isEmptyObject(all)) {
				window.localStorage.removeItem(STORE_KEY);
			} else {
				window.localStorage.setItem(STORE_KEY, JSON.stringify(all));
			}
		} catch (e) { /* private window, storage full: the list still lives while the page does */ }
	}

	function save() {
		var all = readStore();
		var mine = [];
		for (var i = 0; i < queue.length; i++) {
			var r = queue[i];
			mine.push({ id: r.id, url: r.url, data: r.data, json: r.json, barrier: r.barrier, ts: r.ts });
		}
		if (mine.length) {
			all[tabId] = { ts: Date.now(), records: mine };
			if (!heartbeat) { heartbeat = setInterval(save, HEARTBEAT_MS); }
		} else {
			delete all[tabId];
			if (heartbeat) { clearInterval(heartbeat); heartbeat = null; }
		}
		writeStore(all);
	}

	function adoptClosedTabs() {
		var all = readStore();
		var now = Date.now();
		var adopted = [];
		var changed = false;
		for (var key in all) {
			if (!Object.prototype.hasOwnProperty.call(all, key)) { continue; }
			var entry = all[key];
			if (!entry || !entry.ts || now - entry.ts < TAB_ALIVE_MS) { continue; }
			var recs = entry.records || [];
			for (var i = 0; i < recs.length; i++) {
				if (recs[i] && recs[i].url && now - recs[i].ts < MAX_AGE_MS) { adopted.push(recs[i]); }
			}
			delete all[key];
			changed = true;
		}
		if (changed) { writeStore(all); }
		adopted.sort(function (a, b) { return a.ts - b.ts; });
		for (var j = 0; j < adopted.length; j++) {
			var a = adopted[j];
			queue.push({ id: a.id, url: a.url, data: a.data || {}, json: !!a.json, barrier: !!a.barrier,
				ts: a.ts, state: "new", onSuccess: null, d: $.Deferred() });
		}
		if (adopted.length) {
			save();
			pump();
		}
	}

	// ── what the server says about restarts ──────────────────────────────────────────────────────
	var serverState = "running";   // running | pending | begins
	var pollFailing = false;       // the last ask got no answer at all
	var updatingSeen = false;      // "begins" was said and the server has not said "running" since
	var pollTimer = null;
	var polling = false;
	var lastPollAt = 0;

	function poll() {
		if (pollTimer) { clearTimeout(pollTimer); pollTimer = null; }
		if (polling) { return; }
		polling = true;
		lastPollAt = Date.now();
		$.ajax("/app-status", {
			type: "GET", dataType: "json", cache: false, timeout: 20000,
			global: false,   // never the page's own error handling (util.js's 401 → landing)
			success: function (d) {
				pollFailing = false;
				setState((d && d.state) || "running");
			},
			error: function (xhr) {
				// The limiter's 429 says the server is there; only silence counts as failing.
				if (!xhr || xhr.status !== 429) { pollFailing = true; }
				updateBanner();
			},
			complete: function () {
				polling = false;
				schedulePoll();
			}
		});
	}

	function schedulePoll() {
		if (pollTimer) { clearTimeout(pollTimer); }
		var fast = serverState !== "running" || pollFailing || updatingSeen || hasWaiting();
		pollTimer = setTimeout(poll, fast ? POLL_FAST_MS : POLL_SLOW_MS);
	}

	// A failed record makes the next ask come sooner: it is what says the server is back.
	function pollSoon() {
		if (polling) { return; }
		if (Date.now() - lastPollAt >= POLL_FAST_MS) {
			poll();
		} else {
			schedulePoll();
		}
	}

	function setState(state) {
		serverState = state === "pending" || state === "begins" ? state : "running";
		if (serverState === "begins") { updatingSeen = true; }
		if (serverState === "running") {
			updatingSeen = false;
			// The server answered, so whatever is waiting can go now rather than on its timer.
			if (hasWaiting()) { retryStep = 0; retryNow(); }
		}
		updateBanner();
	}

	function updating() {
		return serverState === "begins" || updatingSeen;
	}

	// ── the banner ───────────────────────────────────────────────────────────────────────────────
	var banner = null;

	function updateBanner() {
		var key = null;
		if (updating()) {
			key = "connUpdating";
		} else if (waitingLong()) {
			key = "connLost";
		}
		if (!key) {
			if (banner) { banner.removeClass("plConnBannerOnClass"); }
			return;
		}
		if (!banner) {
			banner = $("<div>", { id: "connBannerId", "class": "plConnBannerClass", role: "status",
				"aria-live": "polite" }).appendTo(document.body);
		}
		banner.text(getRes(key)).addClass("plConnBannerOnClass");
	}

	// ── start ────────────────────────────────────────────────────────────────────────────────────
	adoptClosedTabs();
	$(function () {
		// Once at load — a page opened during a restart says so at once — then on the timer.
		poll();
		document.addEventListener("visibilitychange", function () {
			if (document.visibilityState === "visible" && Date.now() - lastPollAt >= POLL_FAST_MS) {
				poll();
			}
		});
		window.addEventListener("online", function () { pollSoon(); retryNow(); });
		window.addEventListener("pagehide", save);
	});

	window.PlConn = {
		post: post,
		newId: newId,
		// Practice and Learn ask this before starting a NEW exercise: once a restart has begun, one
		// started now would be cut off before its first answers could be saved.
		blocksNewExercise: updating
	};
})();
